import { ThemeEvents } from '@theme/events';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { Component } from '@theme/component';

/**
 * @typedef {{ quantity: number, price: number }} PriceBreak
 * @typedef {{ subtotalValue?: HTMLElement }} ProductSubtotalRefs
 */
class ProductSubtotal extends Component {
  /** @type {PriceBreak[]} */
  #priceBreaks = [];
  #abortController = new AbortController();
  #priceObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#parsePriceBreaks();
    this.#attachEventListeners();
    this.#watchWholesalePrice();
    this.updateSubtotal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#priceObserver?.disconnect();
  }

  #attachEventListeners() {
    const { signal } = this.#abortController;
    const closestSection = this.closest('.shopify-section, dialog');

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
  }

  #watchWholesalePrice() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    this.#priceObserver = new MutationObserver((mutations) => {
      const shouldUpdate = mutations.some((mutation) => {
        if (!(mutation.target instanceof Node)) return false;

        const targetElement =
          mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;

        return Boolean(
          targetElement?.closest(
            '.product-price-inventory__line--current .price, .product-price-inventory__line--current .wpd-ext-compare-at-price'
          )
        );
      });

      if (shouldUpdate) {
        this.updateSubtotal();
      }
    });

    this.#priceObserver.observe(closestSection, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  #parsePriceBreaks() {
    const basePrice = Number(this.dataset.variantPrice || 0);
    const minQuantity = parseInt(this.dataset.minQuantity || '', 10) || 1;
    const priceBreaksData = this.dataset.priceBreaks || '[]';

    this.#priceBreaks = [];

    if (basePrice > 0) {
      this.#priceBreaks.push({ quantity: minQuantity, price: basePrice });
    }

    try {
      const breaks = JSON.parse(priceBreaksData);
      for (const entry of breaks) {
        const quantity = parseInt(entry.quantity, 10);
        const price = Number(entry.price);
        if (!Number.isNaN(quantity) && !Number.isNaN(price)) {
          this.#priceBreaks.push({ quantity, price });
        }
      }
    } catch (_error) {
      this.#priceBreaks = this.#priceBreaks.filter(Boolean);
    }

    this.#priceBreaks.sort((a, b) => b.quantity - a.quantity);
  }

  #handleQuantityUpdate = (event) => {
    const form = this.closest('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    this.updateSubtotal();
  };

  #handleCartUpdate = () => {
    this.updateSubtotal();
  };

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const newSubtotal = event.detail.data.html.querySelector(`product-subtotal[data-block-id="${this.dataset.blockId}"]`);
    if (!(newSubtotal instanceof HTMLElement)) return;

    this.dataset.variantId = newSubtotal.dataset.variantId || '';
    this.dataset.variantPrice = newSubtotal.dataset.variantPrice || '0';
    this.dataset.minQuantity = newSubtotal.dataset.minQuantity || '1';
    this.dataset.priceBreaks = newSubtotal.dataset.priceBreaks || '[]';
    this.dataset.moneyFormat = newSubtotal.dataset.moneyFormat || this.dataset.moneyFormat || '{{amount}}';
    this.dataset.currencyCode = newSubtotal.dataset.currencyCode || this.dataset.currencyCode || '';

    this.#parsePriceBreaks();
    this.#priceObserver?.disconnect();
    this.#watchWholesalePrice();
    this.updateSubtotal();
  };

  #getQuantityInput() {
    const form = this.closest('product-form-component');
    return /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));
  }

  #getCurrentQuantity() {
    const quantityInput = this.#getQuantityInput();
    if (!quantityInput) return 1;

    return parseInt(quantityInput.value || '1', 10) || 1;
  }

  #getCartQuantity() {
    const quantityInput = this.#getQuantityInput();
    if (!quantityInput) return 0;

    return parseInt(quantityInput.getAttribute('data-cart-quantity') || '0', 10) || 0;
  }

  #getWholesaleUnitPrice() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return null;

    const priceElement = closestSection.querySelector('.product-price-inventory__line--current .price');
    if (!(priceElement instanceof HTMLElement)) return null;

    const priceClone = priceElement.cloneNode(true);
    if (!(priceClone instanceof HTMLElement)) return null;

    priceClone.querySelectorAll('.wpd-ext-compare-at-price, s').forEach((node) => node.remove());

    const priceText = priceClone.textContent?.trim();
    if (!priceText) return null;

    const currencyCode = this.dataset.currencyCode || 'USD';
    const wholesalePrice = convertMoneyToMinorUnits(priceText, currencyCode);

    return Number.isFinite(wholesalePrice) ? wholesalePrice : null;
  }

  #getUnitPrice() {
    const wholesaleUnitPrice = this.#getWholesaleUnitPrice();
    if (wholesaleUnitPrice !== null) {
      return wholesaleUnitPrice;
    }

    if (!this.#priceBreaks.length) {
      return Number(this.dataset.variantPrice || 0);
    }

    const totalQuantity = this.#getCurrentQuantity() + this.#getCartQuantity();
    const matchedBreak = this.#priceBreaks.find((priceBreak) => totalQuantity >= priceBreak.quantity);

    return matchedBreak?.price || this.#priceBreaks[this.#priceBreaks.length - 1]?.price || 0;
  }

  updateSubtotal() {
    if (!this.refs.subtotalValue) return;

    const subtotal = this.#getUnitPrice() * this.#getCurrentQuantity();
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currencyCode = this.dataset.currencyCode || 'USD';

    this.refs.subtotalValue.textContent = formatMoney(subtotal, moneyFormat, currencyCode);
  }
}

if (!customElements.get('product-subtotal')) {
  customElements.define('product-subtotal', ProductSubtotal);
}
